import React from "react";

import cx from "classnames";

import Tooltip from "~/ui/Tooltip";

import styles from "./css/Tabs.module.css";

interface TabProps<T> {
	name: T;
	active: boolean;
	onClick: (name: T) => void;
	disabled?: boolean;
	disabledTooltip?: string;
}

export function Tab<T extends string>({ name, active, onClick, disabled, disabledTooltip }: TabProps<T>) {
	// eslint-disable-next-line no-underscore-dangle
	const _onClick = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			if (disabled) return;
			onClick(name);
		},
		[onClick, name, disabled],
	);

	const tabContent = (
		<a
			href={`#${name}`}
			className={cx(styles.tab, {
				[styles.tabActive]: active,
				[styles.tabHover]: !disabled,
			})}
			onClick={_onClick}
		>
			{name}
		</a>
	);

	if (disabled && disabledTooltip) {
		return (
			<Tooltip content={disabledTooltip} placement="top">
				{tabContent}
			</Tooltip>
		);
	}

	return tabContent;
}

interface TabsProps {
	children: React.ReactNode;
	className?: string;
	small?: boolean;
}

export function Tabs({ children, className, small }: React.PropsWithChildren<TabsProps>) {
	return <nav className={cx(styles.tabs, className, { [styles.small]: small })}>{children}</nav>;
}

interface SimpleTabsProps<TabName extends string, Tabs extends readonly TabName[]> {
	tab: TabName;
	tabs: Tabs;
	setTab: (name: TabName) => void;
	className?: string;
	small?: boolean;
	disabledTabs?: Partial<Record<TabName, { disabled: boolean; tooltip?: string }>>;
}

export default function SimpleTabs<TabName extends string, Tabs extends readonly TabName[]>({
	tab,
	tabs,
	setTab,
	className,
	small,
	disabledTabs,
}: SimpleTabsProps<TabName, Tabs>) {
	const onClick = React.useCallback(
		(tab: TabName) => {
			if (disabledTabs?.[tab]?.disabled) return;
			setTab(tab);
		},
		[setTab, disabledTabs],
	);

	return (
		<Tabs className={className} small={small}>
			{tabs.map(tabname => (
				<Tab
					key={tabname}
					name={tabname}
					active={tab === tabname}
					onClick={onClick}
					disabled={disabledTabs?.[tabname]?.disabled}
					disabledTooltip={disabledTabs?.[tabname]?.tooltip}
				/>
			))}
		</Tabs>
	);
}
